import { useMutation } from "@tanstack/react-query";
import type { CreditPackId } from "@/lib/payments";
import { getPackName, getPackPriceUsd } from "@/lib/payments/client";
import { FREE_PREVIEW_CHECKOUT_SELECTION_KEY } from "@/lib/free-preview-funnel";
import {
  trackCheckoutCreationFailed,
  trackCheckoutStarted,
} from "@/lib/posthog-client";
import { logError } from "@/lib/logger";

interface CheckoutResponse {
  checkoutUrl: string;
  checkoutId: string;
}

export type CheckoutSource =
  | "results_view"
  | "fullscreen_modal"
  | "free_preview_comparison"
  | "generation_controls"
  | "sticky_generate_cta"
  | "credits_page"
  | "landing_pricing";

interface CheckoutParams {
  packId: CreditPackId;
  companyId?: string;
  selectedGenerationId?: string;
  source?: CheckoutSource;
  previewNumber?: 1 | 2;
}

async function createCheckout(params: CheckoutParams): Promise<CheckoutResponse> {
  const { packId, companyId, selectedGenerationId, source, previewNumber } = params;

  if (selectedGenerationId) {
    try {
      window.sessionStorage.setItem(
        FREE_PREVIEW_CHECKOUT_SELECTION_KEY,
        selectedGenerationId
      );
    } catch {
      // The return URL also carries the selection when browser storage is unavailable.
    }
  }

  trackCheckoutStarted({
    packId,
    packName: getPackName(packId),
    price: getPackPriceUsd(packId),
    selectedGenerationId,
    source,
    previewNumber,
  });

  let response: Response;
  try {
    response = await fetch("/api/checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        packId,
        companyId,
        selectedGenerationId,
        source,
        previewNumber,
      }),
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "Network error";
    trackCheckoutCreationFailed({
      packId,
      selectedGenerationId,
      source,
      previewNumber,
      status: 0,
      error: message,
    });
    throw error;
  }

  if (!response.ok) {
    const error = await response.json();
    const message = error.error || "Failed to create checkout";
    trackCheckoutCreationFailed({
      packId,
      selectedGenerationId,
      source,
      previewNumber,
      status: response.status,
      error: message,
    });
    throw new Error(message);
  }

  return response.json();
}

export function useCheckout() {
  return useMutation({
    mutationFn: createCheckout,
    onSuccess: (data) => {
      // Use setTimeout to defer redirect, allowing React to finish any pending DOM updates
      // This prevents "removeChild" errors when React is unmounting during navigation
      // Small delay ensures React has finished its current render cycle
      setTimeout(() => {
        try {
          window.location.href = data.checkoutUrl;
        } catch (error) {
          // Fallback: try using replace if href fails
          logError(error, {
            tags: {
              route: "/api/checkout",
              method: "POST",
            },
            extra: {
              checkoutUrl: data.checkoutUrl,
            },
          });
          try {
            window.location.replace(data.checkoutUrl);
          } catch (replaceError) {
            logError(replaceError, {
              tags: {
                route: "/api/checkout",
                method: "POST",
              },
              extra: {
                checkoutUrl: data.checkoutUrl,
              },
            });
            // Last resort: open in new window
            window.open(data.checkoutUrl, "_blank", "noopener,noreferrer");
          }
        }
      }, 50); // 50ms delay to ensure React has finished rendering
    },
  });
}
